import React, { useCallback, useMemo, useState } from 'react';
import { ThemeProvider as BulkThemeProvider } from '../../theme/ThemeProvider';
import { useBulkUpdate } from './useBulkUpdate';
// import PendingChangesView from '../../features/patches/components/PendingChangesView';
import { Container, Snackbar, useMediaQuery } from '@mui/material';
import { H1, HeaderWrapper, MoaDashboardHeader } from '../MoaDashboard/MoaDashboard.styles';
import { Step1SelectStores, Step3ReviewAndSubmit, Step4PublishAndReview, StepDealLovers } from './Steps';
import StepExclusionReconcile from './Steps/StepExclusionReconcile';
import { BulkUpdateStepper, BulkUpdateActions, BulkUpdateDialogs, BulkUpdateNotifications, BulkFlowIndicator } from './components';
import { BulkFlowStep } from './components/BulkFlowIndicator';
import { OperationDrawer } from '../../features/patches';

const BulkUpdate: React.FC = () => {
  const isMobile = useMediaQuery(`(max-width: 1024px)`);
  
  // Bulk flow state
  const [bulkFlowStep, setBulkFlowStep] = useState<BulkFlowStep>('upload-review');

  // Note: the shared store cache intentionally persists across navigation (60m
  // TTL with background refresh), so returning to this page is instant.

  // Callback to handle bulk flow state changes
  const handleBulkFlowStateChange = useCallback((currentStep: BulkFlowStep) => {
    setBulkFlowStep(currentStep);
  }, []);

  // Use the custom hook that contains all the logic
  const {
    addPendingOp,
    clearPendingOps,
    showOperationDrawer,
    operationDrawerMode,
    notification,
    selectedAction,
    showConfirmationModal,
    pendingPatchType,
    hasFetchedPatches,
    showSubmitToast,
    success,
    submitError,
    isSubmitting,
    jobStatus,
    menuJobStatus,
    isMenuJobPolling,
    getPatchDataForType,
    getPatchIdsForType,
    publishConfirmed,
    error,
    setError,
    activeStep,
    bulkUpdateMode,
    selectedFranchises,
    selectedStores,
    selectedPatchType,
    handleBack,
    handleReset,
    handleFranchiseChange,
    handleStoreSelection,
    handleCloseSubmitToast,
    getPatchTypeDisplayName,
    handleBulkUpdateModeChange,
    handlePatchTypeChange,
    handleActionChange,
    dealLoversPrices,
    handleDealLoversPriceChange,
    handleSubmitOperationDrawer,
    handleRemoveOperation,
    handleCloseNotification,
    handleCloseConfirmationModal,
    handleConfirmationModalConfirm,
    closeOperationDrawer,
    handleNext,
    handlePublishAndVerify,
    exclusionWorkflow,
  } = useBulkUpdate();

  // Show pending changes in the UI (enriched ops)
  // const showPendingChanges = pendingChanges && pendingChanges.data.setOperations.length > 0;

  // Dynamic steps based on selected action
  const getSteps = useCallback(() => {
    if (selectedAction === 'review') {
      return ['Select Method & Stores', 'Review'];
    } else if (selectedAction === 'deal-lovers') {
      return ['Select Method & Stores', 'Deal Lovers Pricing', 'Publish & Review'];
    } else if (selectedAction === 'exclusions') {
      return ['Select Method & Stores', 'Reconcile Exclusions', 'Publish & Review'];
    } else {
      return ['Select Method & Stores', 'Manage Operations', 'Publish & Review'];
    }
  }, [selectedAction]);

  const steps = getSteps();

  // Memoised because it is a dependency of the reconcile step's search callback; a fresh array each render
  // would churn it.
  const exclusionStoreNumbers = useMemo(
    () => selectedStores.map((store) => store.storeNumber),
    [selectedStores]
  );

  // Enhanced mode change handler to reset bulk flow state
  const handleModeChange = useCallback((mode: 'manual' | 'bulk-upload') => {
    handleBulkUpdateModeChange(mode);
    setBulkFlowStep('upload-review');
  }, [handleBulkUpdateModeChange]);

  // Handler to dismiss the centralized error
  const handleCloseError = useCallback(() => {
    setError(null);
  }, [setError]);

  /**
   * Not memoised, deliberately.
   *
   * This was a useCallback whose dependency array had drifted to omit 17 of the values its body reads -
   * jobStatus, success, publishConfirmed and submitError among them - so it returned JSX built from a stale
   * closure. The exclusion flow surfaced it: picking a bundle updated the form, but the Search button never
   * enabled, because the step kept receiving a workflow snapshot whose target was still null.
   *
   * The memo bought nothing anyway. This is called during render, so the JSX is rebuilt on every render
   * regardless; only the function identity was being cached, and nothing depends on that. Removing it makes
   * the staleness impossible rather than one more name to remember to add.
   */
  const renderStepContent = (step: number) => {
      // Use localSelectedAction for mode switching
  if (selectedAction === 'review') {
        switch (step) {
          case 0:
            return (
                  <Step1SelectStores
                    selectedFranchises={selectedFranchises}
                    selectedStores={selectedStores}
                    handleFranchiseChange={handleFranchiseChange}
                    handleStoreSelection={handleStoreSelection}
                    bulkUpdateMode={bulkUpdateMode}
                    onBulkUpdateModeChange={handleModeChange}
                    onBulkFlowStateChange={handleBulkFlowStateChange}
                    selectedAction={selectedAction}
                    isMobile={isMobile}
                    handleActionChange={handleActionChange}
                  />
            );
          case 1:
            return (
              <Step3ReviewAndSubmit
                selectedAction={selectedAction}
                selectedStores={selectedStores}
                selectedFranchises={selectedFranchises}
                isSubmitting={isSubmitting}
                hasFetchedPatches={hasFetchedPatches}
                selectedPatchType={selectedPatchType}
                getPatchIdsForType={getPatchIdsForType}
                hideActions={true}
                // PendingChangesView now uses Redux state directly
                clearPendingOps={clearPendingOps}
                handleRemoveOperation={handleRemoveOperation}
                handleSubmitOperationDrawer={handleSubmitOperationDrawer}
                handlePatchTypeChange={handlePatchTypeChange}
              />
            );
          default:
            return null;
        }
      }

      // Exclusion reconcile: select -> search & decide -> publish.
      //
      // Three steps, not four. An earlier cut inserted Step3ReviewAndSubmit as a review step, which
      // dragged the Manage Operations surface in with it - a patch type selector and an Add/Remove
      // Operation drawer, neither of which applies once the target and the per-store decisions are
      // settled. Worse, it fed PatchList the manual flow's selectedPatchType, which is unrelated to the
      // chosen exclusion, so it reported "No patches found" every time. The staged operations are shown
      // on the reconcile step instead.
      if (selectedAction === 'exclusions') {
        switch (step) {
          case 0:
            return (
              <Step1SelectStores
                selectedFranchises={selectedFranchises}
                selectedStores={selectedStores}
                handleFranchiseChange={handleFranchiseChange}
                handleStoreSelection={handleStoreSelection}
                bulkUpdateMode={bulkUpdateMode}
                onBulkUpdateModeChange={handleModeChange}
                onBulkFlowStateChange={handleBulkFlowStateChange}
                selectedAction={selectedAction}
                isMobile={isMobile}
                handleActionChange={handleActionChange}
              />
            );
          case 1:
            return (
              <StepExclusionReconcile
                storeNumbers={exclusionStoreNumbers}
                workflow={exclusionWorkflow}
              />
            );
          case 2:
            return (
              <Step4PublishAndReview
                jobStatus={jobStatus}
                menuJobStatus={menuJobStatus}
                isMenuJobPolling={isMenuJobPolling}
                selectedStores={selectedStores}
                submitError={submitError}
                isSubmitting={isSubmitting}
                success={success}
                publishConfirmed={publishConfirmed}
                onPublishAndVerify={handlePublishAndVerify}
              />
            );
          default:
            return null;
        }
      }

      // Deal Lovers workflow: Step 0 (select) -> Step 1 (pricing) -> Step 2 (publish)
      if (selectedAction === 'deal-lovers') {
        switch (step) {
          case 0:
            return (
              <Step1SelectStores
                selectedFranchises={selectedFranchises}
                selectedStores={selectedStores}
                handleFranchiseChange={handleFranchiseChange}
                handleStoreSelection={handleStoreSelection}
                bulkUpdateMode={bulkUpdateMode}
                onBulkUpdateModeChange={handleModeChange}
                onBulkFlowStateChange={handleBulkFlowStateChange}
                selectedAction={selectedAction}
                isMobile={isMobile}
                handleActionChange={handleActionChange}
              />
            );
          case 1:
            return (
              <StepDealLovers
                dealLoversPrices={dealLoversPrices}
                onPriceChange={handleDealLoversPriceChange}
              />
            );
          case 2:
            return (
              <Step4PublishAndReview
                jobStatus={jobStatus}
                menuJobStatus={menuJobStatus}
                isMenuJobPolling={isMenuJobPolling}
                selectedStores={selectedStores}
                submitError={submitError}
                isSubmitting={isSubmitting}
                success={success}
                publishConfirmed={publishConfirmed}
                onPublishAndVerify={handlePublishAndVerify}
              />
            );
          default:
            return null;
        }
      }

      // Edit workflow: Step 0 -> Step 1 -> Step 2
  switch (step) {
        case 0:
          return (
            <Step1SelectStores
              selectedFranchises={selectedFranchises}
              selectedStores={selectedStores}
              handleFranchiseChange={handleFranchiseChange}
              handleStoreSelection={handleStoreSelection}
              bulkUpdateMode={bulkUpdateMode}
              onBulkUpdateModeChange={handleModeChange}
              onBulkFlowStateChange={handleBulkFlowStateChange}
              selectedAction={selectedAction}
              isMobile={isMobile}
              handleActionChange={handleActionChange}
            />
          );
        case 1:
          return (
            <Step3ReviewAndSubmit
              selectedAction={selectedAction}
              selectedStores={selectedStores}
              selectedFranchises={selectedFranchises}
              isSubmitting={isSubmitting}
              hasFetchedPatches={hasFetchedPatches}
              selectedPatchType={selectedPatchType}
              getPatchIdsForType={getPatchIdsForType}
              getPatchDataForType={getPatchDataForType}
              // PendingChangesView now uses Redux state directly
              clearPendingOps={clearPendingOps}
              handleRemoveOperation={handleRemoveOperation}
              handleSubmitOperationDrawer={handleSubmitOperationDrawer}
              handlePatchTypeChange={handlePatchTypeChange}
            />
          );
        case 2:
          return (
            <Step4PublishAndReview
              jobStatus={jobStatus}
              menuJobStatus={menuJobStatus}
              isMenuJobPolling={isMenuJobPolling}
              selectedStores={selectedStores}
              submitError={submitError}
              isSubmitting={isSubmitting}
              success={success}
              publishConfirmed={publishConfirmed}
              onPublishAndVerify={handlePublishAndVerify}
            />
          );
        default:
          return null;
      }
  };

  return (
    <BulkThemeProvider>
      <MoaDashboardHeader>
        <HeaderWrapper data-testid='BulkUpdatesTitle'>
          <H1>Bulk Updates & Review</H1>
        </HeaderWrapper>
      </MoaDashboardHeader>
      <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
        {/* Show stepper for manual mode */}
        {bulkUpdateMode === 'manual' && (
          <BulkUpdateStepper steps={steps} activeStep={activeStep} />
        )}
        
        {/* Show flow indicator for bulk upload mode */}
        {bulkUpdateMode === 'bulk-upload' && (
          <BulkFlowIndicator 
            currentStep={bulkFlowStep} 
          />
        )}

        {/* PendingChangesView is now rendered inside Step3ReviewAndSubmit for correct placement */}

        {renderStepContent(activeStep)}

        <BulkUpdateActions
          activeStep={activeStep}
          steps={steps}
          onBack={handleBack}
          onReset={handleReset}
          isSubmitting={isSubmitting}
          selectedStores={selectedStores}
          bulkUpdateMode={bulkUpdateMode}
          selectedAction={selectedAction}
          hasStagedChanges={
            selectedAction !== 'exclusions' ||
            (exclusionWorkflow?.counts?.toAdd ?? 0) + (exclusionWorkflow?.counts?.toRemove ?? 0) > 0
          }
          onNext={handleNext}
        />

        <BulkUpdateNotifications
          notification={notification}
          showSubmitToast={showSubmitToast}
          onCloseSubmitToast={handleCloseSubmitToast}
          success={success}
          submitError={submitError}
          onCloseNotification={handleCloseNotification}
        />

        {showOperationDrawer && (
          <OperationDrawer 
            mode={operationDrawerMode} 
            patchType={selectedPatchType} 
            onClose={closeOperationDrawer}
            availablePatches={getPatchDataForType ? getPatchDataForType(selectedPatchType) : []}
            selectedStores={selectedStores}
            getPatchIdsForType={getPatchIdsForType}
            addPendingOp={addPendingOp}
          />
        )}

        <BulkUpdateDialogs
          open={showConfirmationModal}
          pendingPatchType={pendingPatchType}
          getPatchTypeDisplayName={getPatchTypeDisplayName}
          onClose={handleCloseConfirmationModal}
          onConfirm={handleConfirmationModalConfirm}
        />

        {/* Centralized error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <div
            style={{
              background: '#f44336',
              color: 'white',
              padding: '16px',
              borderRadius: '4px',
              minWidth: 300,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              fontWeight: 500,
              fontSize: 16,
              textAlign: 'center',
            }}
            role="alert"
          >
            {error?.message || ''}
            {error?.type === 'network' && (
              <>
                <br />
                <button
                  style={{
                    marginTop: 8,
                    background: 'white',
                    color: '#f44336',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 16px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </>
            )}
          </div>
        </Snackbar>
      </Container>
    </BulkThemeProvider>
  );
};

export default BulkUpdate;
